import React, {ReactNode, useState} from 'react'
import {CheckedIcon, CheckedP} from '../components/header/checked-styles'
import {CrumbSwitcher} from '../components/header/crumb'
import {EnvironmentFilter} from '../components/header/environment-filter'
import {
  EnvironmentTitle,
  OrganisationTitle
} from '../components/header/environment-switcher'
import {
  DropdownBody,
  DropdownItem,
  DropdownSection
} from '../components/library/dropdown/legacy'
import {Dropdown} from '../components/library/dropdown/dropdown'
import {Icon} from '../components/library/icon/icon'
import styled from 'styled-components'
import {
  OrganisationAccess,
  OrganisationAccessEnvironment
} from '../data/workbench-api/generated/types'
import {useSearchRegex} from '../hooks/use-search-regex'

type PluginEnvironmentSwitcherProps = {
  organisations: OrganisationAccess[]
  organisationId: string | undefined
  environmentId: string | undefined
  setEnv: (o: string, e: string) => void
}

const OrgIcon = styled.span`
  color: ${({theme}) => theme.grey35};
  margin-right: 10px;
`

export const PluginEnvironmentSwitcher = ({
  organisations,
  organisationId,
  environmentId,
  setEnv
}: PluginEnvironmentSwitcherProps) => {
  const currentOrg = organisations.find(org => org.id === organisationId)
  const currentEnv = currentOrg?.environments?.find(
    env => env?.id === environmentId
  )

  const [search, setSearch] = useState('')
  return (
    <Dropdown
      disabled={!organisations.length}
      trigger={(handleClick, ref) => (
        <a onClick={handleClick} ref={ref as React.Ref<HTMLAnchorElement>}>
          <CrumbSwitcher>
            {currentOrg && currentEnv ? (
              <>
                {currentOrg.name}&nbsp;
                <span style={{fontWeight: 'bold'}}>{currentEnv.name}</span>
              </>
            ) : (
              'Choose environment'
            )}
          </CrumbSwitcher>
        </a>
      )}
    >
      {closeDropdown => (
        <>
          <DropdownBody style={{minWidth: '220px'}}>
            <EnvironmentFilter value={search} onChange={setSearch} />
            {organisations.length ? (
              organisations.map(org => (
                <PluginEnvironmentSwitcherList
                  key={org.id}
                  organisation={org}
                  currentEnvironmentId={currentEnv?.id}
                  closeDropdown={closeDropdown}
                  setEnv={setEnv}
                  search={search}
                />
              ))
            ) : (
              <div>No organisations</div>
            )}
          </DropdownBody>
        </>
      )}
    </Dropdown>
  )
}

type PluginEnvironmentSwitcherListProps = {
  organisation: OrganisationAccess
  closeDropdown: () => void
  currentEnvironmentId?: string
  setEnv: (o: string, e: string) => void
  search: string
}

const PluginEnvironmentSwitcherList = ({
  organisation,
  closeDropdown,
  currentEnvironmentId,
  setEnv,
  search
}: PluginEnvironmentSwitcherListProps) => {
  const searchRegex = useSearchRegex(search)
  const environments = organisation.environments?.filter(
    (env): env is OrganisationAccessEnvironment =>
      !!env && (!searchRegex || searchRegex.test(env.name))
  )

  if (!environments?.length) return null
  return (
    <DropdownSection>
      <OrganisationTitle>
        <span style={{display: 'flex', alignContent: 'center'}}>
          <OrgIcon>
            <Icon name="building" height="20px" width="20px" />
          </OrgIcon>
          {organisation.name}
        </span>
      </OrganisationTitle>
      {!environments || !environments.length ? (
        <DropdownItem padded={true}>No environments found</DropdownItem>
      ) : (
        environments.map(env => {
          const active = env.id === currentEnvironmentId
          let display: ReactNode = env.name
          if (search && searchRegex) {
            // Split the name into parts, and wrap the parts that match the search text <strong>
            const parts = env.name.split(searchRegex)

            display = parts.map((part, index) =>
              searchRegex.test(part) ? (
                <strong key={index}>{part}</strong>
              ) : (
                part
              )
            )
          }
          return (
            <EnvironmentTitle
              key={`${env.id}-${search}`}
              className={active ? 'active' : ''}
            >
              <a
                onClick={() => {
                  setEnv(organisation.id, env.id)
                  closeDropdown()
                }}
              >
                {active ? (
                  <>
                    <CheckedP>{display}</CheckedP>
                    <CheckedIcon>
                      <Icon name="check" />
                    </CheckedIcon>
                  </>
                ) : (
                  <span>{display}</span>
                )}
              </a>
            </EnvironmentTitle>
          )
        })
      )}
    </DropdownSection>
  )
}
